import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { 
  Wallet, 
  Heart, 
  Star, 
  CheckCircle, 
  Clock, 
  Users, 
  Sparkles,
  ChevronRight,
  Upload,
  X,
  Loader2,
  MessageSquare,
  Shield,
  Zap,
  ArrowRight,
  CreditCard,
  Gem,
  Gift,
  Briefcase,
  User,
  Award,
  Scissors
} from 'lucide-react';
import { toast } from 'sonner';

// Wallet types
const WALLET_TYPES = [
  { value: 'bifold', label: 'Portafoglio bifold (classico)', icon: Wallet },
  { value: 'trifold', label: 'Portafoglio trifold', icon: Wallet },
  { value: 'portacarte', label: 'Portacarte / Card holder', icon: CreditCard },
  { value: 'portamonete', label: 'Portamonete', icon: Gem },
  { value: 'money_clip', label: 'Fermasoldi / Money clip', icon: Wallet },
  { value: 'zip', label: 'Portafoglio con zip', icon: Wallet },
  { value: 'travel', label: 'Portafoglio da viaggio / Organizer', icon: Briefcase },
  { value: 'donna', label: 'Portafoglio donna (continental)', icon: Wallet },
  { value: 'minimalista', label: 'Portafoglio minimalista', icon: CreditCard },
  { value: 'rfid', label: 'Portafoglio con protezione RFID', icon: Shield },
  { value: 'altro', label: 'Altro', icon: Wallet },
];

// Leather/Material types
const MATERIALS = [
  { value: 'vacchetta', label: 'Pelle vacchetta', icon: Wallet },
  { value: 'vitello', label: 'Pelle di vitello', icon: Star },
  { value: 'nabuk', label: 'Nabuk', icon: Sparkles },
  { value: 'coccodrillo', label: 'Pelle di coccodrillo', icon: Gem },
  { value: 'struzzo', label: 'Pelle di struzzo', icon: Gem },
  { value: 'pitone', label: 'Pelle di pitone', icon: Gem },
  { value: 'saffiano', label: 'Pelle saffiano', icon: Briefcase },
  { value: 'vegetale', label: 'Pelle conciata al vegetale', icon: Heart },
  { value: 'vegan', label: 'Pelle vegana / Eco-leather', icon: Heart },
  { value: 'tessuto', label: 'Tessuto pregiato', icon: Scissors },
  { value: 'non_so', label: 'Non so, vorrei consiglio', icon: Sparkles },
];

// Colors
const COLORS = [
  { value: 'nero', label: 'Nero', icon: Wallet },
  { value: 'marrone', label: 'Marrone', icon: Wallet },
  { value: 'cognac', label: 'Cognac / Cuoio', icon: Star },
  { value: 'blu', label: 'Blu navy', icon: Wallet },
  { value: 'bordeaux', label: 'Bordeaux', icon: Gem },
  { value: 'verde', label: 'Verde', icon: Heart },
  { value: 'grigio', label: 'Grigio', icon: Wallet },
  { value: 'bicolore', label: 'Bicolore / Due tonalità', icon: Sparkles },
  { value: 'personalizzato', label: 'Colore personalizzato', icon: Sparkles },
  { value: 'non_so', label: 'Non so, vorrei consiglio', icon: Heart },
];

// Personalization options
const PERSONALIZATIONS = [
  { value: 'iniziali', label: 'Iniziali incise', icon: User },
  { value: 'nome', label: 'Nome completo', icon: User },
  { value: 'logo', label: 'Logo aziendale', icon: Briefcase },
  { value: 'dedica', label: 'Dedica / Frase', icon: Heart },
  { value: 'data', label: 'Data speciale', icon: Gift },
  { value: 'disegno', label: 'Disegno personalizzato', icon: Sparkles },
  { value: 'nessuna', label: 'Nessuna personalizzazione', icon: Wallet },
];

// Occasion
const OCCASIONS = [
  { value: 'uso_personale', label: 'Uso personale / Quotidiano', icon: User },
  { value: 'regalo_lui', label: 'Regalo per lui', icon: Gift },
  { value: 'regalo_lei', label: 'Regalo per lei', icon: Gift },
  { value: 'matrimonio', label: 'Matrimonio / Testimone', icon: Heart },
  { value: 'laurea', label: 'Laurea', icon: Award },
  { value: 'compleanno', label: 'Compleanno', icon: Gift },
  { value: 'business', label: 'Business / Professionale', icon: Briefcase },
  { value: 'anniversario', label: 'Anniversario', icon: Heart },
  { value: 'altro', label: 'Altro', icon: Wallet },
];

// Budget options
const BUDGET_OPTIONS = [
  { value: '30-60', label: '€30 - €60', description: 'Portacarte semplice' },
  { value: '60-100', label: '€60 - €100', description: 'Portafoglio base' },
  { value: '100-180', label: '€100 - €180', description: 'Pelle pregiata' },
  { value: '180-300', label: '€180 - €300', description: 'Alta pelletteria' },
  { value: '300-500', label: '€300 - €500', description: 'Pelli esotiche' },
  { value: '500+', label: '€500+', description: 'Lusso / Su misura totale' },
];

// Timeline options
const TIMELINE_OPTIONS = [
  { value: '1week', label: '1 Settimana', description: 'Urgente' },
  { value: '2weeks', label: '2 Settimane', description: 'Prioritario' },
  { value: '3weeks', label: '3 Settimane', description: 'Standard' },
  { value: '1month', label: '1 Mese', description: 'Con calma' },
  { value: 'flexible', label: 'Flessibile', description: 'Nessuna urgenza' },
];

// Example wallets
const WALLET_EXAMPLES = [
  {
    idea: "Portafoglio per il mio fidanzato con le sue iniziali",
    result: "Bifold in pelle vacchetta color cognac con iniziali incise in oro, cuciture a mano",
    image: "https://images.unsplash.com/photo-1627123424574-724758594e93?w=400&h=300&fit=crop",
    category: "Regalo"
  },
  {
    idea: "Portacarte minimalista per uso quotidiano",
    result: "Card holder in pelle saffiano nera, 6 slot carte, tasca centrale per banconote",
    image: "https://images.unsplash.com/photo-1606503825008-909a67e63c3d?w=400&h=300&fit=crop",
    category: "Minimalista"
  },
  {
    idea: "Portafoglio elegante per occasioni speciali",
    result: "Bifold in pelle di vitello bordeaux, interno in nabuk, protezione RFID integrata",
    image: "https://images.unsplash.com/photo-1559563458-527698bf5295?w=400&h=300&fit=crop",
    category: "Elegante"
  }
];

// Testimonials
const TESTIMONIALS = [
  {
    text: "Ho regalato un portafoglio personalizzato a mio marito per il nostro anniversario. La qualità della pelle è eccezionale, le iniziali incise sono perfette. È rimasto senza parole!",
    author: "Giulia M.",
    location: "Milano"
  },
  {
    text: "Cercavo un portacarte minimalista ma di qualità. L'artigiano ha capito esattamente cosa volevo e il risultato supera ogni aspettativa. Pelle morbidissima!",
    author: "Luca R.",
    location: "Roma"
  },
  {
    text: "Per la laurea di mio figlio volevo un regalo speciale. Il portafoglio con la data incisa è un pezzo unico che conserverà per sempre. Artigianato italiano vero.",
    author: "Roberto B.",
    location: "Firenze"
  }
];

// FAQ items
const FAQ_ITEMS = [
  {
    question: "Che tipo di pelle è migliore per un portafoglio?",
    answer: "La pelle vacchetta conciata al vegetale è la scelta classica: si ammorbidisce con l'uso e sviluppa una bellissima patina. Per un look più elegante, la pelle di vitello è ideale. Le pelli esotiche (coccodrillo, struzzo) sono per chi cerca l'esclusività."
  },
  {
    question: "Quanto tempo ci vuole per realizzare un portafoglio su misura?",
    answer: "Un portafoglio artigianale richiede generalmente 2-3 settimane. Per personalizzazioni complesse o pelli esotiche, calcola 3-4 settimane. I portacarte semplici possono essere pronti in 1-2 settimane."
  },
  {
    question: "Posso scegliere i colori dell'interno?",
    answer: "Assolutamente sì! Puoi personalizzare colore esterno, interno, cuciture e finiture. Molti clienti scelgono combinazioni come esterno nero con interno bordeaux o cognac."
  },
  {
    question: "Come funziona l'incisione delle iniziali?",
    answer: "Le iniziali possono essere incise a caldo (impresse nella pelle), a laser, o con foglia d'oro/argento. L'artigiano ti consiglierà la tecnica migliore in base al tipo di pelle e al risultato desiderato."
  },
  {
    question: "Il portafoglio include protezione RFID?",
    answer: "La protezione RFID può essere aggiunta a qualsiasi portafoglio su richiesta. È una fodera speciale che blocca la lettura non autorizzata delle carte contactless."
  }
];

export default function PortafogliRequestPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    wallet_type: '',
    material: '',
    color: '',
    personalization: '',
    occasion: '',
    budget: '',
    timeline: '',
    attachments: []
  });
  
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    document.title = 'Portafogli Su Misura | Pelletteria Artigianale | Rarifex';
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (formData.attachments.length + files.length > 5) {
      toast.error('Puoi caricare massimo 5 immagini');
      return;
    }
    
    setUploadingFile(true);
    
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error('Solo immagini sono permesse');
        continue;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Immagine troppo grande (max 5MB)');
        continue;
      }
      
      try {
        const fileName = `wallet-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const filePath = `requests/${fileName}`;
        
        const { error } = await supabase.storage
          .from('rarifex-uploads')
          .upload(filePath, file);
        
        if (error) throw error;
        
        const { data: { publicUrl } } = supabase.storage
          .from('rarifex-uploads')
          .getPublicUrl(filePath);
        
        setFormData(prev => ({
          ...prev,
          attachments: [...prev.attachments, publicUrl]
        }));
      } catch (error) {
        console.error('Upload error:', error);
        toast.error('Errore nel caricamento');
      }
    }
    
    setUploadingFile(false);
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.description.trim()) {
      toast.error('Descrivi il portafoglio che desideri');
      return;
    }
    
    if (!user) {
      setShowAuthPrompt(true);
      localStorage.setItem('rarifex_pending_wallet_request', JSON.stringify(formData));
      return;
    }
    
    setLoading(true);
    
    try {
      const walletTypeLabel = WALLET_TYPES.find(t => t.value === formData.wallet_type)?.label || '';
      const materialLabel = MATERIALS.find(t => t.value === formData.material)?.label || '';
      const colorLabel = COLORS.find(t => t.value === formData.color)?.label || '';
      const personalizationLabel = PERSONALIZATIONS.find(t => t.value === formData.personalization)?.label || '';
      const occasionLabel = OCCASIONS.find(p => p.value === formData.occasion)?.label || '';
      const budgetLabel = BUDGET_OPTIONS.find(b => b.value === formData.budget)?.label || '';
      const timelineLabel = TIMELINE_OPTIONS.find(t => t.value === formData.timeline)?.label || '';
      
      // Build formatted description with all form data
      let fullDescription = formData.description;
      
      const details = [];
      if (walletTypeLabel) details.push(`👛  Tipo: ${walletTypeLabel}`);
      if (materialLabel) details.push(`🧵  Materiale: ${materialLabel}`);
      if (colorLabel) details.push(`🎨  Colore: ${colorLabel}`);
      if (personalizationLabel) details.push(`✨  Personalizzazione: ${personalizationLabel}`);
      if (occasionLabel) details.push(`🎁  Occasione: ${occasionLabel}`);
      if (budgetLabel) details.push(`💰  Budget: ${budgetLabel}`);
      if (timelineLabel) details.push(`⏱️  Tempi: ${timelineLabel}`);
      
      if (details.length > 0) {
        const separator = '━━━━━━━━━━━━━━━━━━━━━━━';
        fullDescription += `\n\n${separator}\n✨ DETTAGLI RICHIESTA\n${separator}\n` + details.join('\n');
      }
      
      const requestData = {
        client_id: user.id,
        title: formData.title || `${walletTypeLabel} - ${materialLabel}`.trim() || 'Portafoglio su misura',
        description: fullDescription,
        category: 'Portafogli',
        price_range: formData.budget || 'flexible',
        timeline: formData.timeline || 'flexible',
        attachments: formData.attachments,
      };
      
      const response = await fetch(`${API_URL}/api/requests/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Errore durante la creazione');
      }
      
      toast.success('Richiesta inviata! Riceverai presto proposte dai pellettieri.');
      navigate('/requests');
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error('Errore nell\'invio. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('wallet-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>Portafogli Su Misura | Pelletteria Artigianale Italiana | Rarifex</title>
        <meta name="description" content="Portafogli su misura in pelle italiana realizzati da maestri pellettieri. Personalizza materiale, colore, incisioni. Artigianato Made in Italy. Gratuito e senza impegno." />
        <meta name="keywords" content="portafoglio su misura, portafoglio pelle, portafoglio artigianale, pelletteria italiana, portafoglio personalizzato, portacarte, portafoglio uomo, portafoglio donna" />
        <link rel="canonical" href="https://www.rarifex.com/portafogli-su-misura" />
        <meta property="og:title" content="Portafogli Su Misura | Pelletteria Artigianale Italiana | Rarifex" />
        <meta property="og:description" content="Portafogli su misura in pelle italiana realizzati da maestri pellettieri. Gratuito e senza impegno." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rarifex.com/portafogli-su-misura" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-amber-50/50 via-white to-orange-50/30">
        
        {/* HERO SECTION */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-amber-100/50 via-transparent to-transparent" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                
                {/* Left: Hero Content */}
                <div className="text-center lg:text-left">
                  <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
                    <Wallet className="w-4 h-4" />
                    Pelletteria Su Misura
                  </div>
                  
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                    Il portafoglio perfetto{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-orange-600">
                      creato per te
                    </span>
                  </h1>
                  
                  <p className="text-xl md:text-2xl text-slate-600 mb-8 leading-relaxed">
                    Maestri pellettieri italiani creano il portafoglio dei tuoi sogni.
                  </p>
                  
                  <p className="text-lg text-slate-500 mb-8">
                    Pelli pregiate, cuciture a mano, personalizzazioni uniche.
                    <br />
                    <strong className="text-slate-700">Gratuito e senza impegno.</strong>
                  </p>
                  
                  {/* Trust badges */}
                  <div className="flex flex-wrap justify-center lg:justify-start gap-4 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span>Artigiani verificati</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-amber-500" />
                      <span>Risposte in 24-48h</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-slate-500" />
                      <span>100% Made in Italy</span>
                    </div>
                  </div>
                </div>
                
                {/* Right: Form Card */}
                <div id="wallet-form" className="relative">
                  <Card className="p-6 md:p-8 shadow-2xl border-0 bg-white/80 backdrop-blur-sm">
                    <form onSubmit={handleSubmit} className="space-y-5">
                      
                      {/* Title */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Titolo del tuo progetto
                        </Label>
                        <Input
                          placeholder="Es: Portafoglio regalo anniversario"
                          value={formData.title}
                          onChange={(e) => handleInputChange('title', e.target.value)}
                          className="mt-1.5"
                          data-testid="wallet-title-input"
                        />
                      </div>
                      
                      {/* Description */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Descrivi il portafoglio che desideri *
                        </Label>
                        <Textarea
                          placeholder="Racconta la tua idea... Tipo, colore, personalizzazioni, quante carte deve contenere. Allega foto di riferimento se ne hai."
                          value={formData.description}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                          className="mt-1.5 min-h-[120px]"
                          required
                          data-testid="wallet-description-textarea"
                        />
                      </div>
                      
                      {/* Type & Material */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">
                            Tipo di portafoglio
                          </Label>
                          <select
                            value={formData.wallet_type}
                            onChange={(e) => handleInputChange('wallet_type', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            data-testid="wallet-type-select"
                          >
                            <option value="">Seleziona...</option>
                            {WALLET_TYPES.map(type => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium text-slate-700">
                            Materiale
                          </Label>
                          <select
                            value={formData.material}
                            onChange={(e) => handleInputChange('material', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            data-testid="wallet-material-select"
                          >
                            <option value="">Seleziona...</option>
                            {MATERIALS.map(mat => (
                              <option key={mat.value} value={mat.value}>{mat.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Color & Personalization */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Colore</Label>
                          <select
                            value={formData.color}
                            onChange={(e) => handleInputChange('color', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            data-testid="wallet-color-select"
                          >
                            <option value="">Seleziona...</option>
                            {COLORS.map(col => (
                              <option key={col.value} value={col.value}>{col.label}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Personalizzazione</Label>
                          <select
                            value={formData.personalization}
                            onChange={(e) => handleInputChange('personalization', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            data-testid="wallet-personalization-select"
                          >
                            <option value="">Seleziona...</option>
                            {PERSONALIZATIONS.map(pers => (
                              <option key={pers.value} value={pers.value}>{pers.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Occasion & Budget */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Occasione</Label>
                          <select
                            value={formData.occasion}
                            onChange={(e) => handleInputChange('occasion', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            data-testid="wallet-occasion-select"
                          >
                            <option value="">Seleziona...</option>
                            {OCCASIONS.map(occ => (
                              <option key={occ.value} value={occ.value}>{occ.label}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Budget</Label>
                          <select
                            value={formData.budget}
                            onChange={(e) => handleInputChange('budget', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            data-testid="wallet-budget-select"
                          >
                            <option value="">Seleziona...</option>
                            {BUDGET_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label} - {opt.description}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Timeline */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">Tempi di consegna</Label>
                        <select
                          value={formData.timeline}
                          onChange={(e) => handleInputChange('timeline', e.target.value)}
                          className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                          data-testid="wallet-timeline-select"
                        >
                          <option value="">Seleziona...</option>
                          {TIMELINE_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label} - {opt.description}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      {/* Attachments */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Foto di riferimento o ispirazioni
                        </Label>
                        <div className="mt-1.5">
                          {formData.attachments.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-2">
                              {formData.attachments.map((url, idx) => (
                                <div key={idx} className="relative group">
                                  <img src={url} alt="" className="w-16 h-16 object-cover rounded-lg" />
                                  <button
                                    type="button"
                                    onClick={() => removeAttachment(idx)}
                                    className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {formData.attachments.length < 5 && (
                            <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-amber-300 hover:bg-amber-50/50 transition-colors">
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handleFileUpload}
                                className="hidden"
                                disabled={uploadingFile}
                                data-testid="wallet-file-upload"
                              />
                              {uploadingFile ? (
                                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                              ) : (
                                <Upload className="w-5 h-5 text-slate-400" />
                              )}
                              <span className="text-sm text-slate-500">
                                Carica foto o ispirazioni ({formData.attachments.length}/5)
                              </span>
                            </label>
                          )}
                        </div>
                      </div>
                      
                      {/* Submit Button */}
                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full py-6 text-lg font-semibold bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white shadow-lg shadow-amber-200"
                        data-testid="wallet-submit-button"
                      >
                        {loading ? (
                          <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        ) : (
                          <Wallet className="w-5 h-5 mr-2" />
                        )}
                        Ricevi proposte gratis
                      </Button>
                      
                    </form>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 2: PROBLEMA */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8">
                I portafogli industriali non durano e non emozionano
              </h2>
              
              <div className="space-y-4 text-lg text-slate-600 mb-8">
                <p className="flex items-center justify-center gap-3">
                  <span className="text-slate-400">✗</span>
                  Pelle sintetica che si rovina in pochi mesi
                </p>
                <p className="flex items-center justify-center gap-3">
                  <span className="text-slate-400">✗</span>
                  Tutti uguali, nessuna personalizzazione
                </p>
                <p className="flex items-center justify-center gap-3">
                  <span className="text-slate-400">✗</span>
                  Cuciture che cedono, bottoni che si staccano
                </p>
              </div>
              
              <p className="text-xl text-slate-700 font-medium">
                Un portafoglio artigianale dura una vita e racconta la tua storia.
              </p>
            </div>
          </div>
        </section>

        {/* SECTION 3: SOLUZIONE */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-amber-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Un portafoglio{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-orange-600">
                  unico come te
                </span>
              </h2>
              
              <p className="text-xl text-slate-600 mb-12">
                Maestri pellettieri italiani creano il portafoglio perfetto per te o per chi ami.
                <br />
                <strong className="text-slate-800">Tu scegli tutto, loro cuciono a mano.</strong>
              </p>
              
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-4">
                    <MessageSquare className="w-8 h-8 text-amber-600" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Descrivi il tuo portafoglio</h3>
                  <p className="text-slate-600">Tipo, pelle, colore, personalizzazioni</p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-orange-100 flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-orange-600" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Ricevi proposte</h3>
                  <p className="text-slate-600">Pellettieri esperti ti contattano con preventivi</p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Scegli il tuo artigiano</h3>
                  <p className="text-slate-600">Nessun obbligo, decidi solo se ti convince</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 4: ESEMPI */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-4">
                Da idea a portafoglio perfetto
              </h2>
              <p className="text-xl text-slate-600 text-center mb-12">
                Ecco cosa hanno creato i nostri pellettieri
              </p>
              
              <div className="grid md:grid-cols-3 gap-8">
                {WALLET_EXAMPLES.map((example, idx) => (
                  <Card key={idx} className="overflow-hidden hover:shadow-xl transition-shadow">
                    <img 
                      src={example.image} 
                      alt={example.result}
                      className="w-full h-48 object-cover"
                    />
                    <div className="p-5">
                      <span className="text-xs font-medium text-amber-600 uppercase tracking-wider">
                        {example.category}
                      </span>
                      <p className="text-sm text-slate-500 mt-2 mb-1">
                        Richiesta: "{example.idea}"
                      </p>
                      <p className="font-semibold text-slate-800">
                        → {example.result}
                      </p>
                    </div>
                  </Card>
                ))}
              </div>
              
              <div className="text-center mt-10">
                <Button 
                  onClick={scrollToForm}
                  variant="outline"
                  className="border-amber-200 text-amber-700 hover:bg-amber-50"
                >
                  Vuoi qualcosa di simile? Descrivi la tua idea
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 5: COME FUNZIONA */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-amber-50/50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                In 4 semplici passaggi
              </h2>
              
              <div className="space-y-8">
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 rounded-full bg-amber-600 text-white flex items-center justify-center font-bold text-xl flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Descrivi il portafoglio ideale</h3>
                    <p className="text-slate-600">
                      Racconta cosa cerchi: tipo, pelle, colore, quante carte deve contenere, se vuoi le iniziali incise. Allega foto di riferimento se le hai.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 rounded-full bg-amber-500 text-white flex items-center justify-center font-bold text-xl flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Ricevi proposte dai pellettieri</h3>
                    <p className="text-slate-600">
                      Artigiani specializzati vedono la tua richiesta e ti inviano proposte con pelli, preventivi e tempi di realizzazione.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 rounded-full bg-orange-500 text-white flex items-center justify-center font-bold text-xl flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Scegli e personalizza</h3>
                    <p className="text-slate-600">
                      Confronta le proposte, chatta con gli artigiani, affina i dettagli fino a trovare la soluzione perfetta per te.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 rounded-full bg-green-500 text-white flex items-center justify-center font-bold text-xl flex-shrink-0">
                    4
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Ricevi il tuo portafoglio</h3>
                    <p className="text-slate-600">
                      L'artigiano realizza il portafoglio e te lo spedisce. Un pezzo unico, cucito a mano, che durerà una vita.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 6: PERCHÉ FUNZIONA */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                Perché scegliere un portafoglio artigianale
              </h2>
              
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="flex items-center gap-4 p-4 rounded-xl bg-amber-50">
                  <CheckCircle className="w-8 h-8 text-green-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Dura una vita</h3>
                    <p className="text-sm text-slate-600">La pelle vera migliora con l'uso</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 rounded-xl bg-orange-50">
                  <Sparkles className="w-8 h-8 text-orange-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Unico al mondo</h3>
                    <p className="text-sm text-slate-600">Iniziali, dediche, colori a scelta</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 rounded-xl bg-amber-50">
                  <Scissors className="w-8 h-8 text-amber-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Cucito a mano</h3>
                    <p className="text-sm text-slate-600">Tecniche tradizionali italiane</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 rounded-xl bg-green-50">
                  <Zap className="w-8 h-8 text-green-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Zero rischio</h3>
                    <p className="text-sm text-slate-600">Nessun obbligo di acquisto</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 7: TESTIMONIALS */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-amber-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                Cosa dicono i clienti
              </h2>
              
              <div className="grid md:grid-cols-3 gap-6">
                {TESTIMONIALS.map((testimonial, idx) => (
                  <Card key={idx} className="p-6 bg-white">
                    <div className="flex gap-1 mb-4">
                      {[1,2,3,4,5].map(i => (
                        <Star key={i} className="w-5 h-5 fill-amber-500 text-amber-500" />
                      ))}
                    </div>
                    <p className="text-slate-600 mb-4 italic">
                      "{testimonial.text}"
                    </p>
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center font-semibold text-amber-700">
                        {testimonial.author[0]}
                      </div>
                      <div>
                        <p className="font-medium">{testimonial.author}</p>
                        <p className="text-slate-500 text-xs">{testimonial.location}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 8: FAQ */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                Domande frequenti
              </h2>
              
              <div className="space-y-4">
                {FAQ_ITEMS.map((faq, idx) => (
                  <div 
                    key={idx}
                    className="border border-slate-200 rounded-xl overflow-hidden"
                  >
                    <button
                      onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                      className="w-full flex items-center justify-between p-5 text-left hover:bg-amber-50 transition-colors"
                      data-testid={`wallet-faq-${idx}`}
                    >
                      <span className="font-medium text-slate-800">{faq.question}</span>
                      <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedFaq === idx ? 'rotate-90' : ''}`} />
                    </button>
                    {expandedFaq === idx && (
                      <div className="px-5 pb-5 text-slate-600">
                        {faq.answer}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 9: CTA FINALE */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-amber-100 to-amber-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <Wallet className="w-16 h-16 text-amber-600 mx-auto mb-6" />
              
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Pronto per il tuo portafoglio su misura?
              </h2>
              
              <p className="text-xl text-slate-600 mb-8">
                I maestri pellettieri italiani aspettano la tua richiesta.
              </p>
              
              <Button
                onClick={scrollToForm}
                size="lg"
                className="py-6 px-10 text-lg font-semibold bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white shadow-lg shadow-amber-200"
                data-testid="wallet-cta-final-button"
              >
                <Wallet className="w-5 h-5 mr-2" />
                Ricevi proposte gratis ora
              </Button>
            </div>
          </div>
        </section>

      </div>

      {/* Auth Prompt Modal */}
      {showAuthPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Accedi per continuare</h3>
            <p className="text-slate-600 mb-6">
              Per inviare la tua richiesta e ricevere proposte dai pellettieri, devi prima accedere o creare un account.
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowAuthPrompt(false)}
                className="flex-1"
              >
                Annulla
              </Button>
              <Button
                onClick={() => navigate('/login?redirect=/portafogli-su-misura')}
                className="flex-1 bg-amber-600 hover:bg-amber-700"
              >
                Accedi
              </Button>
            </div>
          </Card>
        </div>
      )}
    </>
  );
}
